import { useCallback, useEffect, useState } from "react";
import {
  Archive,
  Database,
  FileDown,
  FileText,
  Info,
  Loader2,
  ShieldCheck,
} from "lucide-react";
import { Badge, Button } from "./UI";
import LoadProgress from "./LoadProgress";
import CollapsibleSettingsCard from "./CollapsibleSettingsCard";
import { apiFetch, useApp } from "../../context/AppContext";

function formatBytes(bytes) {
  if (!bytes) return "0 B";
  const units = ["B", "KB", "MB", "GB"];
  const power = Math.min(
    Math.floor(Math.log(bytes) / Math.log(1024)),
    units.length - 1,
  );
  const value = bytes / 1024 ** power;
  return `${value >= 10 || power === 0 ? Math.round(value) : value.toFixed(1)} ${units[power]}`;
}

function fileNameFromResponse(response, fallback) {
  const disposition = response.headers.get("content-disposition") || "";
  const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disposition);
  if (!match) return fallback;
  try {
    return decodeURIComponent(match[1]);
  } catch {
    return match[1];
  }
}

async function errorDetail(response, fallback) {
  try {
    const body = await response.json();
    return body?.detail || fallback;
  } catch {
    return fallback;
  }
}

function saveBlob(blob, fileName) {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = fileName;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  // Revoke on the next tick so Safari has committed the download.
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

/**
 * Download the archive while reporting progress.
 *
 * The server builds the ZIP before it sends any bytes, so the download runs
 * in two phases: an indeterminate "preparing" phase, then a real byte-count
 * progress once headers arrive.
 */
async function streamArchive(path, fallbackName, onProgress) {
  const response = await apiFetch(path);
  if (!response.ok) {
    throw new Error(await errorDetail(response, "The export could not be built"));
  }

  const fileName = fileNameFromResponse(response, fallbackName);
  const totalBytes = Number(response.headers.get("content-length")) || 0;
  const records = Number(response.headers.get("x-export-records")) || 0;
  const datasets = Number(response.headers.get("x-export-datasets")) || 0;

  let blob;
  if (!response.body || typeof response.body.getReader !== "function") {
    blob = await response.blob();
  } else {
    const reader = response.body.getReader();
    const chunks = [];
    let received = 0;
    for (;;) {
      // eslint-disable-next-line no-await-in-loop
      const { done, value } = await reader.read();
      if (done) break;
      chunks.push(value);
      received += value.length;
      onProgress({ received, totalBytes });
    }
    blob = new Blob(chunks, { type: "application/zip" });
  }

  saveBlob(blob, fileName);
  return { fileName, records, datasets, bytes: blob.size };
}

export default function DataExportPanel({
  apiBase = "/tenant/settings/data-export",
  title = "Download Organisation Data",
  description = "Export every record of your organisation as CSV files, with an optional PDF report, so you keep a complete copy of your data.",
  tourId,
  defaultExpanded = false,
}) {
  const { showNotification } = useApp();
  const [manifest, setManifest] = useState(null);
  const [loadingManifest, setLoadingManifest] = useState(false);
  const [manifestError, setManifestError] = useState("");
  const [activeFormat, setActiveFormat] = useState(null);
  const [progress, setProgress] = useState(null);

  const loadManifest = useCallback(async () => {
    setLoadingManifest(true);
    setManifestError("");
    try {
      const response = await apiFetch(`${apiBase}/manifest`);
      if (!response.ok) {
        throw new Error(
          await errorDetail(response, "Could not load the export summary"),
        );
      }
      setManifest(await response.json());
    } catch (error) {
      setManifestError(error.message || "Could not load the export summary");
    } finally {
      setLoadingManifest(false);
    }
  }, [apiBase]);

  useEffect(() => {
    loadManifest();
  }, [loadManifest]);

  const handleDownload = async (format) => {
    setActiveFormat(format);
    setProgress(null);
    try {
      const result = await streamArchive(
        `${apiBase}/download?format=${format}`,
        `organisation-data-export.zip`,
        (next) => setProgress(next),
      );
      showNotification(
        `Downloaded ${result.datasets || manifest?.total_datasets || 0} datasets` +
          (result.records ? ` (${result.records.toLocaleString()} records)` : "") +
          ` — ${formatBytes(result.bytes)}`,
        "success",
      );
    } catch (error) {
      showNotification(error.message || "Export failed", "error");
    } finally {
      setActiveFormat(null);
      setProgress(null);
    }
  };

  const busy = activeFormat !== null;
  const percent =
    progress && progress.totalBytes
      ? Math.min(100, Math.round((progress.received / progress.totalBytes) * 100))
      : null;

  const redactedTables = (manifest?.datasets || []).filter(
    (d) => d.redacted_columns?.length,
  );
  const redactedCount = redactedTables.reduce(
    (sum, d) => sum + d.redacted_columns.length,
    0,
  );

  return (
    <CollapsibleSettingsCard
      tourId={tourId}
      title={title}
      description={description}
      icon={<Archive size={18} className="text-white" />}
      iconClassName="bg-gradient-to-br from-teal-500 to-emerald-600"
      defaultExpanded={defaultExpanded}
      headerRight={
        manifest ? (
          <Badge variant="success" size="sm">
            {manifest.total_datasets} datasets
          </Badge>
        ) : null
      }
    >
      <div className="space-y-5">
        {/* Scope summary */}
        {loadingManifest && !manifest && (
          <div className="flex items-center gap-2 text-sm text-slate-500">
            <Loader2 size={14} className="animate-spin" />
            Loading export summary…
          </div>
        )}

        {manifestError && (
          <div className="flex items-start gap-2 p-3 rounded-lg bg-red-50 border border-red-100 text-sm text-red-700">
            <Info size={14} className="mt-0.5 flex-shrink-0" />
            <span>{manifestError}</span>
          </div>
        )}

        {manifest && (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="p-3.5 rounded-xl bg-slate-50 border border-slate-100">
                <p className="text-xs text-slate-400 font-medium uppercase tracking-wide">
                  Organisation
                </p>
                <p className="text-sm font-semibold text-slate-800 mt-0.5 truncate">
                  {manifest.organisation}
                </p>
              </div>
              <div className="p-3.5 rounded-xl bg-slate-50 border border-slate-100">
                <p className="text-xs text-slate-400 font-medium uppercase tracking-wide">
                  Datasets
                </p>
                <p className="text-sm font-semibold text-slate-800 mt-0.5">
                  {manifest.total_datasets} datasets
                </p>
              </div>
              <div className="p-3.5 rounded-xl bg-slate-50 border border-slate-100">
                <p className="text-xs text-slate-400 font-medium uppercase tracking-wide">
                  Scope
                </p>
                <p className="text-sm font-semibold text-slate-800 mt-0.5">
                  {manifest.scope === "full" ? "Whole organisation" : "Your modules"}
                </p>
              </div>
            </div>

            <p className="text-sm text-slate-500">{manifest.scope_label}.</p>

            {manifest.module_labels?.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {manifest.module_labels.map((label) => (
                  <Badge key={label} variant="primary" size="xs">
                    {label}
                  </Badge>
                ))}
              </div>
            )}

            {/* Dataset list */}
            <details className="group rounded-xl border border-slate-100 bg-slate-50/60">
              <summary className="flex items-center gap-2 px-4 py-3 cursor-pointer text-sm font-semibold text-slate-700">
                <Database size={14} className="text-slate-400" />
                What is included
              </summary>
              <div className="max-h-60 overflow-y-auto px-4 pb-3">
                <table className="w-full text-xs">
                  <tbody>
                    {manifest.datasets.map((dataset, index) => (
                      <tr key={dataset.label + index} className="border-t border-slate-100">
                        <td className="py-1.5 pr-3 text-slate-700 font-medium">
                          {dataset.label}
                        </td>
                        <td className="py-1.5 text-right text-slate-400">
                          {dataset.module_label || "Organisation"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </details>

            {redactedCount > 0 && (
              <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-50 border border-amber-100 text-xs text-amber-800">
                <ShieldCheck size={14} className="mt-0.5 flex-shrink-0" />
                <span>
                  {redactedCount} credential fields across {redactedTables.length}{" "}
                  datasets (password hashes, encrypted API keys, session tokens) are
                  replaced with a placeholder. They cannot be used outside this
                  platform, so no usable record is lost.
                </span>
              </div>
            )}
          </>
        )}

        {busy && percent === null && (
          <LoadProgress active label="Preparing archive" durationMs={35000} />
        )}
        {busy && percent !== null && (
          <div className="space-y-2">
            <div className="flex items-center justify-between text-xs font-medium text-slate-600">
              <span className="flex items-center gap-2">
                <Loader2 size={13} className="animate-spin" />
                Downloading… {percent}% complete · {100 - percent}% left
              </span>
              {progress?.received ? (
                <span className="text-slate-400">
                  {formatBytes(progress.received)}
                  {progress.totalBytes ? ` / ${formatBytes(progress.totalBytes)}` : ""}
                </span>
              ) : null}
            </div>
            <div className="h-1.5 w-full rounded-full bg-slate-100 overflow-hidden">
              <div
                className="h-full rounded-full bg-gradient-to-r from-teal-500 to-emerald-600 transition-all duration-200"
                style={{ width: `${percent}%` }}
              />
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex flex-col sm:flex-row gap-3 pt-1">
          <Button
            variant="primary"
            leftIcon={<FileDown size={15} />}
            onClick={() => handleDownload("csv")}
            disabled={busy || !manifest}
            loading={activeFormat === "csv"}
          >
            Download CSV (ZIP)
          </Button>
          <Button
            variant="secondary"
            leftIcon={<FileText size={15} />}
            onClick={() => handleDownload("report")}
            disabled={busy || !manifest}
            loading={activeFormat === "report"}
          >
            Download Report + CSV
          </Button>
        </div>

        <p className="text-xs text-slate-400">
          Both options download a ZIP archive with one CSV per dataset plus a
          machine-readable manifest. “Report + CSV” adds a PDF summary of every
          dataset and its record count inside the same archive. Archives are built on
          demand and are never stored on the server.
        </p>
      </div>
    </CollapsibleSettingsCard>
  );
}
